import { css, cx } from '@linaria/atomic';
import { theme } from '@frontend/theme';

export type SizeProps = 'xxs' | 'xs' | 'small' | 'medium' | 'large' | 'xl' | 'xxl';
type ContainerStylesProps = { size: SizeProps; isInteractive: boolean };

export const sizeStyles: Record<SizeProps, string> = {
  xxs: css`
    font-size: ${theme.font.size.xs};
    font-weight: ${theme.font.weight.bold};
    height: 18px;
    min-height: 18px;
    min-width: 18px;
    width: 18px;
  `,

  xs: css`
    font-size: ${theme.font.size.xs};
    font-weight: ${theme.font.weight.bold};
    height: 24px;
    min-height: 24px;
    min-width: 24px;
    width: 24px;
  `,
  small: css`
    font-size: ${theme.font.size.small};
    font-weight: ${theme.font.weight.bold};
    height: 32px;
    min-height: 32px;
    min-width: 32px;
    width: 32px;
  `,
  medium: css`
    font-size: ${theme.font.size.large};
    font-weight: ${theme.font.weight.bold};
    height: 40px;
    min-height: 40px;
    min-width: 40px;
    width: 40px;
  `,
  large: css`
    font-size: ${theme.font.size.large};
    font-weight: ${theme.font.weight.bold};
    height: 48px;
    min-height: 48px;
    min-width: 48px;
    width: 48px;
  `,

  xl: css`
    font-size: ${theme.font.size.h2};
    font-weight: ${theme.font.weight.bold};
    height: 64px;
    min-height: 64px;
    min-width: 64px;
    width: 64px;
  `,
  xxl: css`
    font-size: ${theme.font.size.h2};
    font-weight: ${theme.font.weight.bold};
    height: 80px;
    min-height: 80px;
    min-width: 80px;
    width: 80px;
  `,
};

export const CONTENT_STYLES = css`
  align-items: center;
  background-color: ${theme.colors.neutral10};
  border-radius: 50%;
  color: ${theme.colors.neutral90};
  display: flex;
  height: 100%;
  justify-content: center;
  line-height: 1;
  overflow: hidden;
  position: relative;
  text-transform: uppercase;
`;

const CONTAINER_BASE = css`
  position: relative;
  /* figure element has default margin styles that need to be removed */
  margin: 0;
`;

const CONTAINER_INTERACTIVE = css`
  &:focus {
    outline: 2px solid ${theme.colors.primary50};
    border-radius: 50%;
  }
  &:hover {
    cursor: pointer;
  }
`;

const CONTAINER_XXS = css``;

const CONTAINER_XS = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: -4px;
    right: -4px;
  }

  #avatar-dot > :first-of-type {
    top: -4px;
    right: -4px;
  }
`;

const CONTAINER_SMALL = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: -4px;
    right: 0px;
  }

  #avatar-dot > :first-of-type {
    top: -5px;
    right: -2px;
  }
`;

const CONTAINER_MEDIUM = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: -5px;
    right: -5px;
  }

  #avatar-dot > :first-of-type {
    top: -4px;
    right: -4px;
  }
`;

const CONTAINER_LARGE = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: 0px;
    right: 0px;
  }

  #avatar-dot > :first-of-type {
    top: 0px;
    right: -1px;
  }
`;

const CONTAINER_XL = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: 1px;
    right: 5px;
  }

  #avatar-dot > :first-of-type {
    top: 1px;
    right: 3px;
  }
`;

const CONTAINER_XXL = css`
  #avatar-bottom-icon > :first-of-type {
    border-radius: 50%;
    position: absolute;
    bottom: 1px;
    right: 5px;
  }

  #avatar-dot > :first-of-type {
    bottom: 1px;
    right: 5px;
  }
`;

const containerSizeMap: Record<SizeProps, string> = {
  xxs: CONTAINER_XXS,
  xs: CONTAINER_XS,
  small: CONTAINER_SMALL,
  medium: CONTAINER_MEDIUM,
  large: CONTAINER_LARGE,
  xl: CONTAINER_XL,
  xxl: CONTAINER_XXL,
};

export const getContainerClassName = ({ size, isInteractive }: ContainerStylesProps): string =>
  cx(CONTAINER_BASE, containerSizeMap[size], isInteractive && CONTAINER_INTERACTIVE);

export const AI_ASSISTANT_STYLES = css`
  border-radius: ${theme.borderRadius.medium};
  background: linear-gradient(
    -90deg,
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5},
    ${theme.colors.primary10},
    ${theme.colors.secondary.seaweed5}
  );

  background-size: 400% 400%;
  animation: gradient 5s ease infinite;

  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }

    50% {
      background-position: 100% 50%;
    }

    100% {
      background-position: 0% 50%;
    }
  }
`;
